function getQuestionSearchMatch(question, query) {
  const search_values = [
    question.question_text,
    question.question_type_label,
    question.question_number,
    question.assignment_label,
  ];
  const is_match = search_values.some((value) => String(value || '').toLowerCase().includes(query));
  return is_match;
}

function resolveSectionIdentity(section) {
  const section_identity = Number(section?.id ?? section?.backend_id ?? null);
  return section_identity;
}

function resolveMainSectionGroupId(main_section) {
  const main_section_group_value = [
    main_section.form_main_section_id,
    main_section.backend_id,
    main_section.id,
    null,
  ].find((value) => value != null);
  const form_main_section_id = Number(main_section_group_value) || null;
  return form_main_section_id;
}

function resolveMainSectionId(main_section) {
  const main_section_id = Number(main_section.main_section_id ?? null) || null;
  return main_section_id;
}

function resolveMainSectionName(main_section) {
  const raw_name = String(main_section.main_section_name || main_section.name || '').trim();
  const main_section_name = raw_name || 'Untitled Main Section';
  return main_section_name;
}

function resolveMainSectionOrder(main_section, index) {
  const order_number = Number(main_section.order_number ?? index + 1);
  return order_number;
}

function resolveVerificationServiceId(ctx) {
  const verification_service_id = String(ctx.verification_active_service_id || '').trim();
  return verification_service_id;
}

function findVerificationService(services, id) {
  const active_service = (services || []).find((service) => {
    const is_match = String(service?.id) === id;
    return is_match;
  }) || null;
  return active_service;
}

function resolveRequiredFields(required_fields_by_service, id) {
  const required = Reflect.get(required_fields_by_service ?? {}, id) || [];
  const normalized_required = Array.isArray(required) ? required : [];
  return normalized_required;
}

function normalizeSectionIndex(ctx, section) {
  const section_id = resolveSectionIdentity(section);
  const section_index = ctx.sections.findIndex((flat_section) => {
    const flat_id = resolveSectionIdentity(flat_section);
    const is_match = flat_id === section_id;
    return is_match;
  });
  return section_index;
}

function buildSectionGroupMeta(main_section, index) {
  const section_group_meta = {
    form_main_section_id: resolveMainSectionGroupId(main_section),
    main_section_id: resolveMainSectionId(main_section),
    main_section_name: resolveMainSectionName(main_section),
    order_number: resolveMainSectionOrder(main_section, index),
  };
  return section_group_meta;
}

function buildNormalizedSections(ctx, sections) {
  return (Array.isArray(sections) ? sections : []).map((section) => {
    const normalized_section = {
      ...section,
      section_index: normalizeSectionIndex(ctx, section),
    };
    return normalized_section;
  });
}

function buildSectionGroup(ctx, main_section, index) {
  const section_group = {
    ...main_section,
    ...buildSectionGroupMeta(main_section, index),
    sections: buildNormalizedSections(ctx, main_section.sections),
  };
  return section_group;
}

function buildFilteredQuestions(ctx, query) {
  const paginated_questions = (Array.isArray(ctx.question_list) ? ctx.question_list : []).filter((question) => {
    if (!query) {
      return true;
    }

    const is_match = getQuestionSearchMatch(question, query);
    return is_match;
  });
  return paginated_questions;
}

export const create_sections_computed = {
  is_verification_form_context() {
    const is_context = Boolean(String(this.verification_form_id || '').trim());
    return is_context;
  },

  verification_active_service() {
    const id = resolveVerificationServiceId(this);
    const active_service = findVerificationService(this.verification_services, id);
    const resolved_service = id ? active_service : null;
    return resolved_service;
  },

  hasMainSection() {
    const has_main_section = this.sections.some((section) => {
      const is_main = section.type === 'main';
      return is_main;
    });
    return has_main_section;
  },

  hasQuestions() {
    const has_questions = this.sections.some((section) => {
      const section_has_questions = Boolean(section.questions && section.questions.length > 0);
      return section_has_questions;
    });
    return has_questions;
  },

  verification_active_required_fields() {
    const id = resolveVerificationServiceId(this);
    const normalized_required = resolveRequiredFields(this.verification_required_fields_by_service, id);
    const required_fields = id ? normalized_required : [];
    return required_fields;
  },

  question_types() {
    const question_types = (this.$store.getters.questionTypes || []).map((type) => {
      const normalized_type = {
        ...type,
        formatted_type: this.format_question_type_label(type.question_type || type.formatted_type || ''),
      };
      return normalized_type;
    });
    return question_types;
  },

  question_types_loading() {
    const is_loading = this.$store.getters.formMetadataLoading;
    return is_loading;
  },

  section_groups() {
    const section_groups = (Array.isArray(this.main_sections) ? this.main_sections : [])
      .map((main_section, index) => {
        const group = buildSectionGroup(this, main_section, index);
        return group;
      })
      .sort((left, right) => {
        const comparison = Number(left.order_number) - Number(right.order_number);
        return comparison;
      });
    return section_groups;
  },

  reusable_main_section_options() {
    const assigned_main_section_ids = new Set(
      (Array.isArray(this.main_sections) ? this.main_sections : [])
        .map((item) => {
          const item_id = Number(item?.main_section_id || 0);
          return item_id;
        })
        .filter(Boolean)
    );
    const options = (Array.isArray(this.reusable_main_sections) ? this.reusable_main_sections : [])
      .filter((item) => {
        const is_available = !assigned_main_section_ids.has(Number(item?.id || 0));
        return is_available;
      });
    return options;
  },

  reusable_sub_section_options() {
    const assigned_section_ids = new Set(
      (Array.isArray(this.sections) ? this.sections : [])
        .map((item) => {
          const item_id = Number(item?.section_id ?? item?.backend_id ?? item?.id ?? 0);
          return item_id;
        })
        .filter(Boolean)
    );
    const options = (Array.isArray(this.reusable_sub_sections) ? this.reusable_sub_sections : [])
      .filter((item) => {
        const is_available = !assigned_section_ids.has(Number(item?.id || 0));
        return is_available;
      });
    return options;
  },

  selected_reusable_main_section() {
    const selected_item = this.reusable_main_section_options.find((item) => {
      const is_match = String(item?.id || '') === String(this.new_main_section.existing_main_section_id || '');
      return is_match;
    }) || null;
    return selected_item;
  },

  selected_reusable_sub_section() {
    const selected_item = this.reusable_sub_section_options.find((item) => {
      const is_match = String(item?.id || '') === String(this.new_sub_section.existing_section_id || '');
      return is_match;
    }) || null;
    return selected_item;
  },

  active_assign_section_label() {
    const section = this.active_assign_section || {};
    const label_candidates = [
      section.name,
      section.section_name,
      section.section?.name,
      section.section?.section_name,
    ];
    const label = label_candidates.find((value) => String(value || '').trim()) || '';
    const normalized_label = String(label).trim() || 'Selected section';
    return normalized_label;
  },

  active_assign_main_section_label() {
    const section = this.active_assign_section || {};
    const label_candidates = [
      section.main_section_name,
      section.form_main_section_name,
      section.main_section?.main_section_name,
      section.main_section?.name,
    ];
    const label = label_candidates.find((value) => String(value || '').trim()) || '';
    const normalized_label = String(label).trim();
    return normalized_label;
  },

  filtered_questions() {
    const query = String(this.question_search || '').trim().toLowerCase();
    return buildFilteredQuestions(this, query);
  },

  paginated_questions() {
    const start = (this.current_page - 1) * this.page_size;
    return this.filtered_questions.slice(start, start + this.page_size);
  },

  total_pages() {
    return Math.max(
      1,
      Math.ceil(this.filtered_questions.length / this.page_size)
    );
  },

  pagination_label() {
    if (!this.filtered_questions.length) {
      return 'No questions available';
    }

    const start = ((this.current_page - 1) * this.page_size) + 1;
    const end = Math.min(
      this.current_page * this.page_size,
      this.filtered_questions.length
    );

    return `Showing ${start} to ${end} of ${this.filtered_questions.length} questions`;
  },
};
